import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Truck, ArrowRight, Clock, ShieldCheck, MapPin, Calculator, Loader2 } from "lucide-react";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  estimateShippingByWeight,
  type ShippingEstimateResult,
} from "@/lib/shipping.functions";
import { COUNTRIES } from "@/lib/greek-regions";
import { formatPrice } from "@/lib/format";
import acsLogoEst from "@/assets/couriers/acs.png";
import eltaLogoEst from "@/assets/couriers/elta.webp";
import boxnowLogoEst from "@/assets/couriers/boxnow.webp";
import skroutzLogoEst from "@/assets/couriers/skroutz-last-mile.webp";
import courierCenterLogoEst from "@/assets/couriers/couriercenter.webp";
import storePickupLogo from "@/assets/couriers/store-pickup.webp";
import boxnowLogo from "@/assets/couriers/boxnow.webp";
import skroutzLastMileLogo from "@/assets/couriers/skroutz-last-mile.webp";
import acsLogo from "@/assets/couriers/acs.png";
import eltaLogo from "@/assets/couriers/elta.webp";
import courierCenterLogo from "@/assets/couriers/couriercenter.webp";
import mrpandaVanLogo from "@/assets/couriers/mrpanda-van.png";
import freightTruckLogo from "@/assets/couriers/freight-truck.png";

type ShippingOption = {
  logo: string;
  emoji: string;
  title: string;
  desc: string;
  highlight: string;
};

const options: ShippingOption[] = [
  {
    logo: storePickupLogo,
    emoji: "🏪",
    title: "Παραλαβή από το κατάστημα",
    desc: "Παράλαβε την παραγγελία σου από το φυσικό μας κατάστημα στη Σταυρούπολη Θεσσαλονίκης — γρήγορα και χωρίς κόστος αποστολής.",
    highlight: "Δωρεάν",
  },
  {
    logo: boxnowLogo,
    emoji: "📦",
    title: "BoxNow",
    desc: "Παράδοση σε αυτόματο μηχάνημα παραλαβής (locker) όλο το 24ωρο, όποτε σε βολεύει.",
    highlight: "24/7 locker",
  },
  {
    logo: skroutzLastMileLogo,
    emoji: "🛵",
    title: "Skroutz Last Mile",
    desc: "Παράδοση σε αυτόματο μηχάνημα παραλαβής (locker) μέσω του δικτύου της Skroutz.",
    highlight: "Locker",
  },
  {
    logo: acsLogo,
    emoji: "🚚",
    title: "ACS Courier",
    desc: "Παράδοση στην πόρτα σου με ACS — χρέωση βάσει βάρους & προορισμού.",
    highlight: "Πανελλαδικά",
  },
  {
    logo: eltaLogo,
    emoji: "✉️",
    title: "ΕΛΤΑ Courier",
    desc: "Παράδοση με ΕΛΤΑ Courier σε όλη την Ελλάδα, ακόμη και σε δυσπρόσιτες περιοχές.",
    highlight: "Παντού",
  },
  {
    logo: courierCenterLogo,
    emoji: "🌍",
    title: "Courier Center",
    desc: "Παράδοση με Courier Center σε Ελλάδα & Κύπρο — ιδανικό για αποστολές εκτός συνόρων.",
    highlight: "Ελλάδα & Κύπρος",
  },
  {
    logo: mrpandaVanLogo,
    emoji: "🐼",
    title: "Παράδοση MrPanda",
    desc: "Παράδοση με δικό μας μεταφορικό μέσο — με την προσωπική φροντίδα της παρέας του Panda!",
    highlight: "Δική μας ομάδα",
  },
  {
    logo: freightTruckLogo,
    emoji: "🏭",
    title: "Μεταφορική του πελάτη",
    desc: "Αποστολή στη μεταφορική εταιρεία που θα μας υποδείξεις — τέλειο για ογκώδεις ή μεγάλες παραγγελίες.",
    highlight: "Για όγκο",
  },
];

const perks = [
  { icon: Clock, title: "Γρήγορη αποστολή", desc: "Ετοιμάζουμε την παραγγελία σου άμεσα." },
  { icon: ShieldCheck, title: "Ασφαλής συσκευασία", desc: "Τα προϊόντα φτάνουν σώα & ασφαλή." },
  { icon: MapPin, title: "Σε όλη την Ελλάδα", desc: "Και στην Κύπρο μέσω Courier Center." },
];

type CourierLine = { logo: string; label: string; serviceable: boolean; cost: number; note?: string };

function ShippingEstimator() {
  const estimate = useServerFn(estimateShippingByWeight);
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState<string>("Ελλάδα");
  const [weight, setWeight] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ShippingEstimateResult | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const weightKg = parseFloat(weight.replace(",", "."));
    if (!city.trim() || !postalCode.trim()) {
      setError("Συμπλήρωσε πόλη και Τ.Κ.");
      return;
    }
    if (!Number.isFinite(weightKg) || weightKg < 0.1 || weightKg > 100) {
      setError("Δώσε έγκυρο βάρος (0,1 – 100 kg).");
      return;
    }
    setLoading(true);
    try {
      const res = await estimate({
        data: { city: city.trim(), postalCode: postalCode.trim(), country, weightKg },
      });
      setResult(res);
    } catch {
      setError("Δεν μπορέσαμε να υπολογίσουμε το κόστος. Δοκίμασε ξανά.");
    } finally {
      setLoading(false);
    }
  };

  const lines: CourierLine[] = result
    ? [
        { logo: acsLogoEst, label: "ACS Courier", serviceable: true, cost: result.acs.cost, note: result.acs.zoneLabel },
        { logo: eltaLogoEst, label: "ΕΛΤΑ Courier", serviceable: true, cost: result.elta.cost, note: result.elta.isThessaloniki ? "Θεσσαλονίκη" : "Πανελλαδικά" },
        { logo: boxnowLogoEst, label: "BoxNow", serviceable: result.boxnow.serviceable, cost: result.boxnow.cost, note: result.boxnow.reason },
        { logo: skroutzLogoEst, label: "Skroutz Last Mile", serviceable: result.skroutz.serviceable, cost: result.skroutz.cost, note: result.skroutz.reason },
        { logo: courierCenterLogoEst, label: "Courier Center", serviceable: true, cost: result.courierCenter.cost, note: result.courierCenter.zoneLabel },
        ...(result.mrpanda.serviceable
          ? [
              {
                logo: mrpandaVanLogo,
                label: "Παράδοση MrPanda",
                serviceable: true,
                cost: result.mrpanda.cost,
                note: (() => {
                  const parts: string[] = ["Θεσσαλονίκη"];
                  if (result.mrpanda.distanceKm != null) {
                    parts.push(`${result.mrpanda.distanceKm} χλμ`);
                  }
                  if (result.mrpanda.freeOver != null) {
                    parts.push(`Δωρεάν πάνω από ${formatPrice(result.mrpanda.freeOver)}`);
                  }
                  return parts.join(" · ");
                })(),
              } satisfies CourierLine,
            ]
          : []),
      ]
    : [];

  return (
    <div className="mt-10 rounded-3xl border border-border bg-card p-6 sm:p-8">
      <div className="flex items-center gap-2">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Calculator className="h-5 w-5" />
        </span>
        <h2 className="text-lg font-semibold">Υπολόγισε το κόστος αποστολής</h2>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Συμπλήρωσε τη διεύθυνση και το βάρος του δέματος για ενδεικτικό κόστος ανά courier.
      </p>

      <form onSubmit={onSubmit} className="mt-5 grid gap-4 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor="est-city">Πόλη</Label>
          <Input id="est-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="π.χ. Θεσσαλονίκη" />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="est-zip">Τ.Κ.</Label>
          <Input id="est-zip" inputMode="numeric" maxLength={5} value={postalCode} onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, "").slice(0, 5))} placeholder="5 ψηφία" />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="est-country">Χώρα</Label>
          <select
            id="est-country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            {COUNTRIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="est-weight">Βάρος (kg)</Label>
          <Input id="est-weight" inputMode="decimal" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="π.χ. 2" />
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={loading} className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
            Υπολόγισε κόστος
          </Button>
        </div>
      </form>

      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

      {result && (
        <div className="mt-6">
          <div className="grid gap-3 sm:grid-cols-2">
            {lines.map((l) => (
              <div key={l.label} className="flex items-center gap-3 rounded-2xl border border-border bg-background p-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-white p-1">
                  <img src={l.logo} alt={l.label} width={36} height={36} className="h-full w-full object-contain" loading="lazy" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">{l.label}</div>
                  {l.serviceable ? (
                    l.note && <div className="text-xs text-muted-foreground">{l.note}</div>
                  ) : (
                    <div className="text-xs text-destructive">{l.note ?? "Μη διαθέσιμο για αυτή την παραγγελία."}</div>
                  )}
                </div>
                {l.serviceable && (
                  <span className="shrink-0 text-base font-bold text-primary">{formatPrice(l.cost)}</span>
                )}
              </div>
            ))}
          </div>
          {result.isCyprus && (
            <p className="mt-3 text-xs text-muted-foreground">
              Για Κύπρο η αποστολή γίνεται μέσω Courier Center.
            </p>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            ℹ️ Ενδεικτικό κόστος — η τελική τιμή υπολογίζεται στο ταμείο βάσει των πραγματικών προϊόντων.
          </p>
        </div>
      )}
    </div>
  );
}

function ShippingMethodsPage() {
  return (
    <StorefrontLayout hideCartSidebar>
      <div className="mx-auto max-w-none px-4 py-10 sm:py-14">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary/10 via-background to-accent/10 p-8 sm:p-12">
          <div className="pointer-events-none absolute -right-6 -top-8 text-8xl opacity-20 select-none">🚚</div>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/15 px-4 py-1.5 text-sm font-medium text-primary">
            <Truck className="h-4 w-4" />
            Διάλεξε πώς θα φτάσει σε σένα
          </span>
          <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
            Τρόποι Αποστολής
          </h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Από lockers 24/7 μέχρι την πόρτα σου με courier — ή ακόμη και με το δικό μας πανταζάκι! 🐼
            Διάλεξε τον τρόπο που σε βολεύει και άσε τα υπόλοιπα σε εμάς.
          </p>
        </div>

        {/* Perks */}
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {perks.map((p, i) => (
            <div key={i} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <p.icon className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-sm font-semibold">{p.title}</span>
                <span className="block text-xs text-muted-foreground">{p.desc}</span>
              </span>
            </div>
          ))}
        </div>

        {/* Options */}
        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {options.map((o, i) => (
            <Card
              key={i}
              className="group overflow-hidden border-border transition-shadow hover:shadow-lg"
            >
              <CardContent className="flex h-full flex-col p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-border bg-background p-2 transition-transform group-hover:scale-110">
                    <img src={o.logo} alt={o.title} width={48} height={48} className="max-h-full max-w-full object-contain" />
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xl" aria-hidden>{o.emoji}</span>
                      <h2 className="text-base font-semibold leading-tight">{o.title}</h2>
                    </div>
                    <span className="mt-1.5 inline-block rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-medium text-accent-foreground">
                      {o.highlight}
                    </span>
                  </div>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{o.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Estimator */}
        <ShippingEstimator />

        {/* CTA */}
        <div className="mt-10 rounded-3xl border border-border bg-muted/40 p-6 text-center sm:p-8">
          <p className="text-2xl">🛒</p>
          <h2 className="mt-2 text-xl font-semibold">Έτοιμος για παραγγελία;</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Τα ακριβή κόστη αποστολής υπολογίζονται αυτόματα στο ταμείο, ανάλογα με το βάρος και τον προορισμό σου.
          </p>
          <Button asChild className="mt-4">
            <Link to="/cart">
              Δες το καλάθι σου
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </StorefrontLayout>
  );
}

export const Route = createFileRoute("/shipping-methods")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://mrpanda.gr/shipping-methods" },
      { title: "Τρόποι Αποστολής — MrPanda.gr" },
      {
        name: "description",
        content:
          "Όλοι οι τρόποι αποστολής του MrPanda.gr: BoxNow, Skroutz Last Mile, ACS, ΕΛΤΑ Courier, Courier Center, παραλαβή από κατάστημα και παράδοση MrPanda.",
      },
      { property: "og:title", content: "Τρόποι Αποστολής — MrPanda.gr" },
      {
        property: "og:description",
        content: "Διάλεξε τον τρόπο αποστολής που σε βολεύει — lockers, courier ή παραλαβή από το κατάστημα.",
      },
    ],
    links: [{ rel: "canonical", href: "https://mrpanda.gr/shipping-methods" }],
  }),
  component: ShippingMethodsPage,
});